import React, { useEffect, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Box, useMediaQuery } from "@mui/material";
import useTheme from "../../../common/hooks/useTheme";
import { useAppStore } from "../../../providers/AppStore";
import { useAuth } from "../../../providers/AuthProvider";
import PlatformSidebar from "../components/navigation/PlatformSidebar";
import {
  SidebarProvider,
  useSidebar,
} from "../components/navigation/SidebarContext";
import PageLoader from "../../reports/components/UI/PageLoader";
import Breadcrumb from "../components/common/Breadcrumb";
import { useClientSidePanel } from "../../../providers/ClientSidePanelProvider";
import { useElevenLabsWidget } from "../../reports/hooks/useElevenLabsWidget";
import { ChatRoot, resolveChatProviderMode } from "../../chat";
import PlatformSnackbars from "./PlatformSnackbars";
import { setCustomerSession } from "../../../common/network/customerSession";
import { clearClientTabName } from "../utils/clientTabs";

const AffiliateShellContent = () => {
  const theme = useTheme();
  const { sidebarWidth } = useSidebar();
  const [isLoading, setIsLoading] = useState(true);
  const {
    ASisLoadingPlatformData,
    ASSetIsLoadingPlatformData,
    ASGetBiomarkersList,
    ASGetLogicCategoriesList,
    ASSetSelectedClient,
  } = useAppStore();
  const { AuthUser } = useAuth();
  const { closePanel } = useClientSidePanel();
  const isDesktop = useMediaQuery("(min-width:601px)");

  const location = useLocation();
  const chatProviderMode = resolveChatProviderMode(location.search);
  const isMockChatMode = chatProviderMode === "mock";
  const isElevenLabsMode = !isMockChatMode;
  const scrollableRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchData = async () => {
      // The affiliate main tab carries no client context: clear anything this
      // tab may hold (e.g. after navigating here from a client workspace) and
      // fetch only the affiliate-global catalogs.
      ASSetSelectedClient(null);
      setCustomerSession(null);
      // Release the window.name too. A tab that reached the client list from a
      // client workspace keeps that client's name, so clicking the same client
      // would target this very tab and load it in place instead of opening one.
      clearClientTabName();
      ASSetIsLoadingPlatformData(false);
      closePanel();

      // The biomarker catalogue (6 MB, ~9 s uncached) is not waited for:
      // nothing on first paint reads it, and its consumers fill in when it
      // lands. It handles its own errors.
      void ASGetBiomarkersList();
      await ASGetLogicCategoriesList();
      setIsLoading(false);
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (scrollableRef.current) {
      scrollableRef.current.scrollTop = 0;
    }
  }, [location]);

  // Always call the hook - inert without a selected client, kept for parity
  // with the workspace shell
  useElevenLabsWidget(
    "",
    AuthUser.agentType,
    null,
    !isLoading && !ASisLoadingPlatformData,
    isElevenLabsMode
  );

  return (
    <div
      className="viewport-height"
      style={{
        display: "flex",
        backgroundColor: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
      }}
    >
      <PlatformSidebar variant="affiliate" />
      {isElevenLabsMode && <div id="chat-container"></div>}
      {isMockChatMode && (
        <ChatRoot surfaceId="platform" providerMode={chatProviderMode} />
      )}
      <div
        ref={scrollableRef}
        className="viewport-height"
        style={{
          flex: 1,
          marginLeft: `${sidebarWidth}px`,
          overflowX: "auto",
          overflowY: "auto",
          backgroundColor: theme.colorTokens.N[10],
          transition: "margin-left 0.3s ease-in-out",
        }}
      >
        <Box
          sx={{
            width: "100%",
            minHeight: "100%",
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
          }}
        >
          {/* Breadcrumb navigation */}
          <Breadcrumb variant="affiliate" />

          {/* Main content */}
          <Box
            sx={{
              width: `${1440 - sidebarWidth}px`,
              padding: "24px 16px",
              flex: 1,
            }}
          >
            {isLoading || ASisLoadingPlatformData ? <PageLoader /> : <Outlet />}
          </Box>
        </Box>
      </div>
      <PlatformSnackbars />
    </div>
  );
};

/**
 * The affiliate main tab: client list, orders, services, walkthroughs,
 * settings. Holds no per-client context — clicking a client in the list opens
 * that client's workspace in a new browser tab (WorkspaceShell mode="client").
 */
const AffiliateShell = () => {
  return (
    <SidebarProvider>
      <AffiliateShellContent />
    </SidebarProvider>
  );
};

export default AffiliateShell;
